import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { EndpointService } from '@app/core';
import { DiaEspecial, DiaFestivo } from '@app/shared';

@Injectable()
export class SpecialDaysDataService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) {}
  /**
   * Retorna todos los registros de dias festivos
   *
   * @returns {Observable<DiaFestivo[]>}
   * @memberof SpecialDaysDataService
   */
  getHolidays(): Observable<DiaFestivo[]> {
    return new Observable(obs => {
      this.http.get<DiaFestivo[]>(this.api.get('holidays')).subscribe(
        dias => {
          const formattedData: any[] = [];
          dias.forEach((d: DiaFestivo) => {
            const f: string[] = d.fecha.split('-');
            const setFecha: string = f[2] + '/' + f[1] + '/' + f[0];
            formattedData.push({
              id: d.id,
              nombre: d.nombre,
              fecha: setFecha
            });
          });
          obs.next(formattedData);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }
  /**
   * Retorna todos los registros de dias especiales
   *
   * @returns {Observable<DiaEspecial[]>}
   * @memberof SpecialDaysDataService
   */
  getSpecialDays(): Observable<DiaEspecial[]> {
    return new Observable(obs => {
      this.http.get<DiaEspecial[]>(this.api.get('specialDays')).subscribe(
        dias => {
          const formattedData: any[] = [];
          dias.forEach((d: DiaEspecial) => {
            let tipoL = '';
            let cboTipo = '';
            const f: string[] = d.fecha.split('-');
            const setFecha: string = f[2] + '/' + f[1] + '/' + f[0];

            if (d.tipo) {
              tipoL = 'Sí';
              cboTipo = 'true';
            } else {
              tipoL = 'No';
              cboTipo = 'false';
            }

            formattedData.push({
              id: d.id,
              nombre: d.nombre,
              fecha: setFecha,
              empresa: d.empresa['id'],
              tipoL: tipoL,
              tipo: d.tipo,
              cboTipo: cboTipo
            });
          });
          obs.next(formattedData);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }
  /**
   * Agrega un dia festivo
   *
   * @param {DiaFestivo} diaFestivo
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  addHolidays(diaFestivo: DiaFestivo): Observable<boolean> {
    let configData: any;

    configData = {
      nombre: diaFestivo.nombre,
      fecha: diaFestivo.fecha
    };

    return new Observable(obs => {
      this.http
        .post(this.api.get('holidays'), configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 201) {
              obs.next(true);
              this.toastr.success('Dia festivo agregado correctamente.', 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            obs.error(err);
          }
        );
    });
  }
  /**
   * Agrega un dia especial
   *
   * @param {DiaEspecial} diaEspecial
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  addSpecialDays(diaEspecial: DiaEspecial): Observable<boolean> {
    let configData: any;

    configData = {
      nombre: diaEspecial.nombre,
      fecha: diaEspecial.fecha,
      empresa: diaEspecial.empresa,
      tipo: diaEspecial.tipo
    };

    return new Observable(obs => {
      this.http.skipErrorHandler()
        .post(this.api.get('specialDays'), configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 201) {
              obs.next(true);
              this.toastr.success('Dia especial agregado correctamente.', 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              if (err.error.fecha) {
                this.toastr.error(err.error.fecha);
              } else if (err.error.detail) {
                this.toastr.error(err.error.detail);
              } else {
                this.toastr.error('Ha ocurrido un error inesperado');
              }
            }
            obs.next(false);
          }
        );
    });
  }
  /**
   * Actualiza un dia festivo
   *
   * @param {DiaFestivo} diaFestivo
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  updateHolidays(diaFestivo: DiaFestivo): Observable<boolean> {
    let configData: any;

    configData = {
      nombre: diaFestivo.nombre,
      fecha: diaFestivo.fecha
    };

    return new Observable(obs => {
      this.http
        .put(this.api.get('holidays', [diaFestivo.id]), configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 201) {
              obs.next(true);
              this.toastr.success('Dia festivo actualizado correctamente.', 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            obs.error(err);
          }
        );
    });
  }
  /**
   * Actualiza un dia especial
   *
   * @param {DiaEspecial} diaEspecial
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  updateSpecialDays(diaEspecial: DiaEspecial): Observable<boolean> {
    let configData: any;

    configData = {
      nombre: diaEspecial.nombre,
      fecha: diaEspecial.fecha,
      empresa: diaEspecial.empresa,
      tipo: diaEspecial.tipo
    };

    return new Observable(obs => {
      this.http.skipErrorHandler()
        .put(this.api.get('specialDays', [diaEspecial.id]), configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 201) {
              obs.next(true);
              this.toastr.success('Dia especial actualizado correctamente.', 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              if (err.error.fecha) {
                this.toastr.error(err.error.fecha);
              } else if (err.error.detail) {
                this.toastr.error(err.error.detail);
              } else {
                this.toastr.error('Ha ocurrido un error inesperado');
              }
            }
            obs.next(false);
          }
        );
    });
  }
  /**
   * elimina un dia festivo
   *
   * @param {DiaFestivo} diaFestivo
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  deleteHolidays(diaFestivo: DiaFestivo): Observable<boolean> {
    return new Observable(obs => {
      this.http
        .delete(this.api.get('holidays', [diaFestivo.id]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 200) {
              obs.next(true);
              this.toastr.success(res.body['message'], 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            obs.error(err);
          }
        );
    });
  }
  /**
   * elimina un dia especial
   *
   * @param {DiaEspecial} diaEspecial
   * @returns {Observable<boolean>}
   * @memberof SpecialDaysDataService
   */
  deleteEspecialDays(diaEspecial: DiaEspecial): Observable<boolean> {
    return new Observable(obs => {
      this.http
        .delete(this.api.get('specialDays', [diaEspecial.id]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 200) {
              obs.next(true);
              this.toastr.success(res.body['message'], 'Operación exitosa');
            } else {
              obs.next(false);
            }
          },
          err => {
            obs.error(err);
          }
        );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/special-days/shared/special-days-data.service.ts